/**
 * Purpose: Modular sticky sidebar for Business Detail — cards render only when
 *          they have content. Future cards (Hours, Amenities — Phase 6 data)
 *          slot in as new <InfoCard> blocks without layout changes.
 * Inputs: business, views.
 * Outputs: column of InfoCards + location card; sticky on lg+ (handled by parent).
 *          The Contact card renders the normalized contact list (D-15): one or
 *          many phones / WhatsApps / emails / websites / social profiles.
 */
import React from 'react';
import InfoCard, { InfoRow } from '../../ui/InfoCard';
import GoogleMapsLocationLink from '../../common/GoogleMapsLocationLink';
import { getBusinessContacts } from '../../../utils/contacts';
import { hasGoogleMapsTarget } from '../../../utils/googleMaps';
import { formatDate } from '../../../utils/helpers';

const PhoneIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);
const PinIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);
const HomeIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

const GlobeIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
    />
  </svg>
);
const MailIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);
const ChatIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

const CONTACT_KIND_META = {
  phone: { label: 'Phone', icon: PhoneIcon },
  whatsapp: { label: 'WhatsApp', icon: ChatIcon },
  email: { label: 'Email', icon: MailIcon },
  website: { label: 'Website', icon: GlobeIcon },
  facebook: { label: 'Facebook', icon: GlobeIcon },
  instagram: { label: 'Instagram', icon: GlobeIcon },
  tiktok: { label: 'TikTok', icon: GlobeIcon },
  youtube: { label: 'YouTube', icon: GlobeIcon },
  linkedin: { label: 'LinkedIn', icon: GlobeIcon },
  other: { label: 'Contact', icon: GlobeIcon },
};

const StickySidebar = ({ business, views = 0 }) => {
  const contacts = getBusinessContacts(business);
  const mapsTarget = {
    latitude: business.latitude,
    longitude: business.longitude,
    address: business.address,
    city: business.city,
    name: business.name,
  };

  return (
    <div className="space-y-4">
      {(contacts.length > 0 || business.address || business.society?.name) && (
        <InfoCard title="Contact">
          {contacts.map((contact, index) => {
            const meta = CONTACT_KIND_META[contact.kind] || CONTACT_KIND_META.other;
            const isExternal = contact.href && /^https?:/i.test(contact.href);
            return (
              <InfoRow
                key={`${contact.kind}-${contact.value}-${index}`}
                icon={meta.icon}
                label={contact.label || meta.label}
                href={contact.href}
                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {contact.value}
              </InfoRow>
            );
          })}
          {business.address && (
            <InfoRow icon={PinIcon} label="Address">
              {business.address}
            </InfoRow>
          )}
          {business.society?.name && (
            <InfoRow icon={HomeIcon} label="Society">
              {business.society.name}
            </InfoRow>
          )}
        </InfoCard>
      )}

      {hasGoogleMapsTarget(mapsTarget) && (
        <GoogleMapsLocationLink variant="card" heading="Location" {...mapsTarget} />
      )}

      <InfoCard title="Quick facts">
        <InfoRow label="Category">
          <span className="capitalize">{(business.category || 'other').replace(/_/g, ' ')}</span>
        </InfoRow>
        <InfoRow label="Views">{views}</InfoRow>
        {business.created_at && <InfoRow label="Listed since">{formatDate(business.created_at)}</InfoRow>}
      </InfoCard>
    </div>
  );
};

export default StickySidebar;
